import { styled } from '@mui/material/styles';
import { Box, List } from '@mui/material';
import { SdfIconButton, SdfLink, SdfSelectSimple, SdfIcon } from '@waypoint/react-components';
import { colors } from '@headless-shell/shared-styles';
import StyledAvatar from '../StyledAvatar';

export const ResponsiveFooterWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isMobileOrTablet',
})(({ theme, isMobileOrTablet }) => ({
  ...(isMobileOrTablet && {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    columnGap: '1rem',
    rowGap: '2rem',
    paddingBottom: '2rem',
    maxWidth: '61.5rem',
    width: '100%',
    paddingLeft: 0,
    paddingRight: 0,
  }),
  ...(!isMobileOrTablet && {
     display: 'flex',
  flexDirection: 'row',
  flexWrap: 'nowrap', // Prevent wrapping
  width: '100%',
  maxWidth: '100%',
  alignItems: 'stretch',
  gap: '1.6rem',
  justifyContent: 'space-between',
  boxSizing: 'border-box',
  }),
  [theme.breakpoints.up('sm')]: {
    columnGap: isMobileOrTablet ? '2rem' : undefined,
  },
  [theme.breakpoints.between('md', 'lg')]: {
    maxWidth: '65.2rem', // Use maxWidth instead of width for responsiveness
    marginLeft: 'auto',
    marginRight: 'auto',
  },
}));

export const FooterContainer = styled(Box)(({ theme }) => ({
  height: 'auto',
  backgroundColor: colors.blue.footerBg,
  padding: '4.4rem 1.5rem 4.4rem 1.5rem',
  gap: '4.06rem',
  width:'100%',
  [theme.breakpoints.up('md')]: {
    paddingLeft: 0,
    paddingRight: 0,
  }
}));

export const FooterMainContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  height: '100%',
  flexWrap: 'nowrap',
  boxSizing:'content-box',
  justifyContent: 'space-between',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'row', // keep single row on small screens
    alignItems: 'flex-start',
    height: 'auto',
    gap: '2rem',
    overflowX: 'auto',
  },
}));

export const FooterInfoContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  height: '100%',
  [theme.breakpoints.down('md')]: {
    height: 'auto',
    minWidth: 0
  },
}));

export const FooterSectionGroup = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '1.5rem',
  [theme.breakpoints.down('md')]: {
    gap: '1rem',
  },
}));

export const SocialMediaContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isMobileOrTablet',
})(({ isMobileOrTablet }) => ({
  ...(isMobileOrTablet && {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    gridColumn: '1 / -1',
    marginTop: '1rem'
    
  }),
}));

export const MarketPlaceHeading = styled('h2')(({theme}) => ({
  color: colors.white.default,
  fontWeight: theme.typography.cmp.weights.bold,
  fontSize: theme.typography.cmp.sizes.base,
  lineHeight: theme.typography.cmp.lineHeights.base,
  boxSizing: 'border-box',
   [theme.breakpoints.down('sm')]: {
    fontSize: '0.95rem', // Reduce font size for iPhone SE
  },
}));

export const MarketPlaceLinksList = styled(List)(() => ({
  margin: 0,
  padding: 0,
  listStyle: 'none',
  display: 'flex',
  flexDirection: 'column',
  gap: 0
}));


export const MarketPlaceListItem = styled('li')(() => ({
  margin: 0,
  padding: 0,
  listStyle: 'none'
}));

export const MarketPlaceLinkItem = styled(SdfLink)(({theme}) => ({
  padding: 0,
  color: colors.white.default,
  display: 'inline-flex',
  fontWeight: theme.typography.cmp.weights.regular,
  paddingTop: '0.5rem',
  fontSize: theme.typography.cmp.sizes.md,
  gap: '0.25rem',
  justifyContent: 'flex-start',
  textDecoration: 'none',
  textAlign: 'left',
  alignSelf: 'flex-start',
  '&:hover': {
    textDecoration: 'none',
    background: 'transparent',
  }
}));


export const FooterBottomContent = styled(Box)(({theme}) => ({
  height: 'inherit',
  color: colors.white.default,
  display: 'flex',
  paddingTop: '5rem',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: '2rem',
  [theme.breakpoints.up( 'lg')]: {
    columnGap: '10rem',
  },
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '1.5rem',
    paddingTop: '1rem',
    textAlign: 'center'
  },
  [theme.breakpoints.between(961, 990)]: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  }
}))


export const LegalTextContainer = styled(Box)(({theme}) => ({
  display: 'block', // <-- Forces each span to a new line
  width: '100%',
  marginBottom: '0.25rem', // Optional: spacing between lines
  fontSize: theme.typography.cmp.sizes.md,
  lineHeight: theme.typography.cmp.lineHeights.base,
  color: colors.white.default,
  marginTop: 0,
  maxWidth: '60rem',
  minWidth: 0,
  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
    textAlign: 'center',
    paddingTop: '1rem',
  },
  [theme.breakpoints.between(961, 990)]: {
    maxWidth: '100%',
    textAlign: 'left'
  }
}));

export const FooterRightContent = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'flex-end',
  paddingRight: "1rem",
  minWidth: 0,
  flex: '1 1 auto',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center'
  },
  [theme.breakpoints.between(961, 990)]: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    textAlign: 'left'
  }
}));

export const FooterLeftContent = styled(Box)(({theme}) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  flexWrap: 'wrap', // <-- Allows wrapping only if items overflow
  minWidth: 0,      // <-- Prevents unwanted wrapping
  width: 'auto',    // <-- Do not force 100% width
  [theme.breakpoints.up('md')]: {
    flexWrap: 'wrap',
  },
  [theme.breakpoints.between('lg', 'xl')]: {
   flexWrap: 'nowrap',
  },
  [theme.breakpoints.between('sm','md')]: {
    marginTop: 0,
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'wrap', // <-- Allows wrapping on medium screens
    gap: '0.25rem',
    width: '100%'
  },
  [theme.breakpoints.down('sm')]: {
    justifyContent: 'center',
    alignItems: 'center'
  },
  [theme.breakpoints.between(961, 990)]: {
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: '0.25rem'
  },
  '.cookie-text': {
    color: colors.black.default,
  }
}));

export const FooterLegalLinks = styled(SdfLink)(({ theme }) => ({
  color: colors.white.default,
  textDecoration: 'none',
  fontSize: theme.typography.cmp.sizes.md,
  fontWeight: theme.typography.cmp.weights.regular,
  display: 'inline-block',
  minWidth: 0,
  flexShrink: 1,
  whiteSpace: 'nowrap',
  lineHeight: theme.typography.cmp.lineHeights.base,
  '&:hover': {
    textDecoration: 'underline',
  },
}));

export const Separator = styled('span')(({ theme }) => ({
  display: 'inline-block',
  whiteSpace: 'nowrap', // Prevent separator from wrapping alone
  lineHeight: theme.typography.cmp.lineHeights.base,
  fontSize: theme.typography.cmp.sizes.md,
  fontWeight: theme.typography.cmp.weights.mediumBold,
  color: colors.white.default,
  padding: '0 0.625rem',
  position: 'relative',
  [theme.breakpoints.down('md')]: {
    marginTop: '1rem',
  },
  top: '0',
  '&::before': {
    content: '"|"',
  }
}));

export const FooterMarketplaceImage = styled(StyledAvatar)(({theme})=>({
  width: '5.18rem',
  height: '3.31rem',
  display: 'block',
  objectFit: 'contain',
  marginRight: '1.5rem',
  marginTop: 0,
  background: 'transparent', 
  boxShadow: 'none',        
  border: 'none',
  [theme.breakpoints.down('md')]: {
    marginTop: '2rem'
  },
  [theme.breakpoints.between(961, 990)]: {
    marginTop: 0
  }
}));

export const AISolutionsBadge = styled('span')(() => ({
  font: 'Circular20',
  fontWeight: 450,
  fontStyle: 'italic',
  color: colors.purple.main,
  paddingLeft: '0.2rem'
}));

export const ShowMoreText = styled(SdfLink)(({theme}) => ({
  cursor: 'pointer',
  color: colors.white.default,
  fontWeight: theme.typography.cmp.weights.regular,
  fontSize: theme.typography.cmp.sizes.md,
  lineHeight: theme.typography.cmp.lineHeights.base,
  paddingTop: '0.5rem',
  textDecoration: 'none',
  '&:hover': {
    textDecoration: 'none',
  }
}));

export const SocialMediaLinks = styled(SdfIconButton)(() => ({
  textDecoration: 'none',
  width: '1.5rem',
  height: '1.5rem',
  marginRight: '1.2rem',
  marginLeft: '0.65rem',
}));


export const StyledSdfIcon = styled(SdfIcon)(() => ({
cursor: 'pointer'
}));

export const StyledSdfSelect =  styled(SdfSelectSimple)({
  width: '100%',
  marginTop: "1.5rem"
});


export const LegalTextSpan = styled(Box)(({ theme }) => ({
  display: 'block',
  whiteSpace: 'normal',
  [theme.breakpoints.up('lg')]: {
    whiteSpace: 'nowrap',
  },
  [theme.breakpoints.between('md', 'xxl')]: {
    whiteSpace: 'normal', 
  },
}));

